import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useApp } from '../context/AppContext';
import { useSubscription } from '../context/SubscriptionContext';
import { getColors, getModalStyle } from '../theme';
import { Plus, X, Edit2, Trash2, Target, ChevronDown, ChevronUp, Check, Calendar, Archive, RotateCcw, Lock, Sparkles, List, LayoutGrid, GitBranch, Link2, Flame } from 'lucide-react';
import { UpgradePrompt, UpgradeLimitModal } from '../components/LockedFeature';
import { GoalsPageSkeleton } from '../components/LoadingSkeleton';
import ConsistencyRing from '../components/ConsistencyRing';
import LinkHabitsPrompt from '../components/LinkHabitsPrompt';
import LinkedHabitsModal from '../components/LinkedHabitsModal';
import { PageHead, InkBox } from '../components/Notebook';
import { calculateGoalConsistency } from '../utils/goalConsistencyUtils';
import SoftUpgradeNudge from '../components/SoftUpgradeNudge';

// SMART goal framework info
const SMART_CRITERIA = [
  { key: 'specific', label: 'Specific', question: 'What exactly will you accomplish?' },
  { key: 'measurable', label: 'Measurable', question: 'How will you know when it is accomplished?' },
  { key: 'achievable', label: 'Achievable', question: 'Is this goal realistic given your resources?' },
  { key: 'relevant', label: 'Relevant', question: 'Why does this goal matter to you?' },
  { key: 'timeBound', label: 'Time-bound', question: 'When will you accomplish this goal?' }
];

// Rotating goal name suggestions
const GOAL_SUGGESTIONS = [
  "Run a marathon",
  "Learn Spanish",
  "Save $10,000",
  "Read 24 books this year",
  "Get promoted at work",
  "Launch a side project",
  "Lose 20 pounds",
  "Travel to 3 new countries",
  "Learn to play guitar",
  "Complete a certification",
];

// View mode options
const VIEW_MODES = [
  { value: 'list', label: 'List', icon: List },
  { value: 'timeline', label: 'Timeline', icon: GitBranch },
  { value: 'board', label: 'Board', icon: LayoutGrid }
];

const GoalsPage = () => {
  const { isDark, goals, archivedGoals, addGoal, updateGoal, deleteGoal, addMilestone, toggleMilestone, deleteMilestone, archiveGoal, restoreGoal, permanentlyDeleteGoal, dataLoading, habits, completions } = useApp();
  const { hasFeature, canAddMore, getItemLimit, loading: subscriptionLoading } = useSubscription();
  const colors = getColors(isDark);
  
  // Check if user can add more goals
  const goalLimit = getItemLimit('maxGoals');
  const canAddGoal = canAddMore('maxGoals', goals.length);
  const isAtLimit = !canAddGoal && goalLimit !== Infinity;
  
  // Check if user has milestones feature
  const hasMilestones = hasFeature('milestones');
  
  // Check if user has SMART goals feature (Pro only)
  const hasSmartGoals = hasFeature('analytics'); // Pro feature
  
  // SMART goal template toggle state
  const [smartModeEnabled, setSmartModeEnabled] = useState(false);
  
  // Rotating goal suggestion index
  const [goalSuggestionIndex, setGoalSuggestionIndex] = useState(0);
  
  // Rotate goal suggestions every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setGoalSuggestionIndex(prev => (prev + 1) % GOAL_SUGGESTIONS.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);
  
  // View mode state - persist in localStorage
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem('goalsViewMode');
    return saved && ['list', 'timeline', 'board'].includes(saved) ? saved : 'list';
  });
  
  // Save view mode to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('goalsViewMode', viewMode);
  }, [viewMode]);
  const [showModal, setShowModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [expandedGoal, setExpandedGoal] = useState(null);
  const [showMilestoneInput, setShowMilestoneInput] = useState(null);
  const [milestoneTitle, setMilestoneTitle] = useState('');
  
  // Completed goals modal state
  const [showArchivedModal, setShowArchivedModal] = useState(false);
  const [restoreGoalId, setRestoreGoalId] = useState(null);
  const [restoreTargetDate, setRestoreTargetDate] = useState('');
  const [archivedSort, setArchivedSort] = useState('newest');

  const [hoveredGoalId, setHoveredGoalId] = useState(null);
  
  // Habit linking state
  const [selectedHabitIds, setSelectedHabitIds] = useState([]);
  const [linkedHabitsModalGoal, setLinkedHabitsModalGoal] = useState(null);
  
  // Filter to only show non-archived habits
  const activeHabits = habits.filter(h => !h.archived);
  
  const [goalForm, setGoalForm] = useState({
    title: '',
    specific: '',
    measurable: '',
    achievable: '',
    relevant: '',
    timeBound: '',
    targetDate: '',
    milestones: []
  });
  
  // State for new milestone input in modal
  const [newMilestoneTitle, setNewMilestoneTitle] = useState('');
  
  // Filter goals (active goals only - completed are in archivedGoals)
  const filteredGoals = goals.filter(g => !g.completed);
  
  // Sort goals by target date
  const sortedGoals = [...filteredGoals].sort((a, b) => {
    if (a.targetDate && b.targetDate) {
      return new Date(a.targetDate) - new Date(b.targetDate);
    }
    if (a.targetDate) return -1;
    if (b.targetDate) return 1;
    return new Date(b.createdAt) - new Date(a.createdAt);
  });
  
  // Modal handlers
  const openAddModal = () => {
    setEditingGoal(null);
    setGoalForm({
      title: '',
      specific: '',
      measurable: '',
      achievable: '',
      relevant: '',
      timeBound: '',
      targetDate: '',
      milestones: []
    });
    setNewMilestoneTitle('');
    setSelectedHabitIds([]);
    setShowModal(true);
  };
  
  const openEditModal = (goal) => {
    setEditingGoal(goal);
    setGoalForm({
      title: goal.title,
      specific: goal.specific || '',
      measurable: goal.measurable || '',
      achievable: goal.achievable || '',
      relevant: goal.relevant || '',
      timeBound: goal.timeBound || '',
      targetDate: goal.targetDate || '',
      milestones: goal.milestones || []
    });
    setNewMilestoneTitle('');
    setSelectedHabitIds(goal.linkedHabitIds || []);
    setShowModal(true);
  };
  
  const handleSave = () => {
    if (!goalForm.title.trim()) return;
    
    if (editingGoal) {
      updateGoal(editingGoal.id, { ...goalForm, linkedHabitIds: selectedHabitIds });
    } else {
      addGoal({ ...goalForm, linkedHabitIds: selectedHabitIds });
    }
    setShowModal(false);
  };
  
  const handleDelete = (id) => {
    deleteGoal(id);
  };
  
  const handleAddMilestone = (goalId) => {
    if (!milestoneTitle.trim()) return;
    addMilestone(goalId, milestoneTitle.trim());
    setMilestoneTitle('');
    setShowMilestoneInput(null);
  };
  
  // Calculate progress
  const getProgress = (goal) => {
    if (!goal.milestones || goal.milestones.length === 0) return 0;
    const completed = goal.milestones.filter(m => m.completed).length;
    return Math.round((completed / goal.milestones.length) * 100);
  };
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  
  // Check if goal is overdue
  const isOverdue = (goal) => {
    if (!goal.targetDate || goal.completed) return false;
    return new Date(goal.targetDate) < new Date(new Date().toDateString());
  };

  // Check if user has access to goals (after all hooks)
  if (subscriptionLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'transparent' }}>
        <div className="w-8 h-8 border-2 rounded-full animate-spin" style={{ borderColor: colors.border, borderTopColor: colors.accent }} />
      </div>
    );
  }
  
  if (!hasFeature('goals')) {
    return <UpgradePrompt feature="Goal Setting" description="Set SMART goals with milestones, track your progress, and achieve more with structured goal management." />;
  }

  // Show skeleton while data is loading
  if (dataLoading) {
    return (
      <div className="min-h-screen pb-16 pt-6" style={{ backgroundColor: 'transparent' }}>
        <GoalsPageSkeleton />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-16" style={{ backgroundColor: 'transparent' }}>
      <div>
        
        <PageHead
          title="Goals"
          meta="SMART goals, milestones, and the habits that feed them"
          actions={
            <>
              <div className="inline-flex" role="radiogroup" aria-label="Goal view" style={{ border: `1px solid ${colors.borderStrong}` }}>
                {VIEW_MODES.map((mode, i) => {
                  const Icon = mode.icon;
                  const isActive = viewMode === mode.value;
                  return (
                    <button
                      key={mode.value}
                      role="radio"
                      aria-checked={isActive}
                      onClick={() => setViewMode(mode.value)}
                      className="flex items-center gap-2 px-3 py-2 text-xs transition-colors duration-150"
                      style={{
                        backgroundColor: isActive ? colors.accent : 'transparent',
                        color: isActive ? colors.onAccent : colors.textSecondary,
                        borderLeft: i ? `1px solid ${colors.borderStrong}` : 'none',
                      }}
                      title={mode.label}
                    >
                      <Icon size={14} strokeWidth={1.5} />
                      <span className="hidden sm:inline">{mode.label}</span>
                    </button>
                  );
                })}
              </div>
              {archivedGoals.length > 0 && (
                <button
                  onClick={() => setShowArchivedModal(true)}
                  className="flex items-center gap-2 px-3 sm:px-4 py-2 text-xs transition-colors duration-150"
                  style={{ color: colors.textSecondary, border: `1px solid ${colors.borderStrong}` }}
                >
                  <Archive size={14} strokeWidth={1.5} />
                  <span className="hidden sm:inline">Completed</span> ({archivedGoals.length})
                </button>
              )}
              <button
                onClick={() => isAtLimit ? setShowUpgradeModal(true) : openAddModal()}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors duration-150 ${isAtLimit ? 'opacity-75' : ''}`}
                style={{ backgroundColor: colors.accent, color: colors.onAccent }}
              >
                <Plus size={14} />
                New goal
                {isAtLimit && <span className="text-[10px]">({goals.length}/{goalLimit})</span>}
              </button>
              {isAtLimit && (
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  className="text-xs font-medium hover:underline"
                  style={{ color: colors.accent }}
                >
                  Upgrade →
                </button>
              )}
            </>
          }
        />
        
        {/* Soft upgrade nudge - shows when approaching limit */}
        <SoftUpgradeNudge 
          current={goals.length}
          limit={goalLimit}
          itemType="goals"
          colors={colors}
          onUpgradeClick={() => setShowUpgradeModal(true)}
        />
        
        {/* Goals Display - Different views based on viewMode */}
        {sortedGoals.length === 0 ? (
          <div 
            className="text-center py-20 rounded-lg "
            style={{ 
              backgroundColor: colors.bgSecondary,
              border: `1px solid ${colors.border}`
            }}
          >
            <Target size={48} style={{ color: colors.textMuted }} className="mx-auto mb-4" />
            <p style={{ color: colors.textMuted }} className="mb-6">
              No goals yet. Set your first goal!
            </p>
            <button
              onClick={openAddModal}
              className="px-6 py-3 text-xs rounded-md transition-all duration-200 font-medium"
              style={{ backgroundColor: colors.accent, color: colors.onAccent }}
            >
              Create Your First Goal
            </button>
          </div>
        ) : viewMode === 'timeline' ? (
          /* Timeline View - Clean vertical timeline with dots */
          <div 
            className="pt-5 pb-2"
            style={{ borderTop: `1px solid ${colors.textPrimary}` }}
          >
            <div className="mb-6">
              <h3 className="text-lg font-semibold tracking-tight" style={{ color: colors.textPrimary }}>
                Your Journey
              </h3>
              <p className="text-xs mt-0.5" style={{ color: colors.textMuted }}>
                Your goals mapped across time
              </p>
            </div>
            
            {/* Timeline container */}
            <div className="relative">
              {/* Timeline items */}
              <div className="space-y-0">
                {sortedGoals.map((goal, index) => {
                  const progress = getProgress(goal);
                  const overdue = isOverdue(goal);
                  const goalColor = progress === 100 ? colors.accent : overdue ? colors.error : colors.accent;
                  const isExpanded = expandedGoal === goal.id;
                  const targetDate = goal.targetDate ? new Date(goal.targetDate) : null;
                  const dateLabel = targetDate 
                    ? `${targetDate.toLocaleDateString('en-US', { month: 'short' })} '${targetDate.getFullYear().toString().slice(-2)}`
                    : 'No date';
                  const isLast = index === sortedGoals.length - 1;
                  
                  return (
                    <div key={goal.id} className="flex">
                      {/* Dot + Line column - hidden on mobile, shown on md+ */}
                      <div className="hidden md:flex flex-shrink-0 w-20 pr-3 pt-3 text-right">
                        <span 
                          className="text-xs font-medium w-full"
                          style={{ color: colors.textMuted }}
                        >
                          {dateLabel}
                        </span>
                      </div>
                      
                      {/* Dot + Line column - hidden on mobile */}
                      <div className="hidden md:flex flex-shrink-0 w-6 flex-col items-center">
                        {/* Dot */}
                        <div 
                          className="w-3 h-3 rounded-full flex-shrink-0 mt-3.5 z-10 transition-all duration-200"
                          style={{ 
                            backgroundColor: progress === 100 ? colors.accent : colors.bg,
                            border: `2px solid ${goalColor}`,
                            boxShadow: progress === 100 ? `0 0 8px ${colors.accent}60` : 'none'
                          }}
                        />
                        {/* Connecting line (not on last item) */}
                        {!isLast && (
                          <div 
                            className="w-px flex-1"
                            style={{ backgroundColor: `${colors.border}60` }}
                          />
                        )}
                      </div>
                      
                      {/* Goal card - full width on mobile */}
                      <div className="flex-1 pb-4 md:pl-3 min-w-0 overflow-hidden">
                        <div 
                          data-goal-card
                          className="pt-3 pb-4 transition-colors duration-200"
                          style={{ 
                            borderTop: `1px solid ${colors.textPrimary}`,
                            maxWidth: '100%'
                          }}
                        >
                          {/* Mobile: Date badge + Progress dot */}
                          <div className="flex items-center gap-2 mb-2 md:hidden">
                            <div 
                              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                              style={{ 
                                backgroundColor: progress === 100 ? colors.accent : colors.bg,
                                border: `2px solid ${goalColor}`,
                                boxShadow: progress === 100 ? `0 0 6px ${colors.accent}60` : 'none'
                              }}
                            />
                            <span 
                              className="text-[10px] font-medium px-2 py-0.5 rounded-full"
                              style={{ 
                                backgroundColor: overdue ? `${colors.error}15` : colors.bgTertiary,
                                color: overdue ? colors.error : colors.textMuted 
                              }}
                            >
                              {dateLabel}
                            </span>
                          </div>
                          
                          {/* Title - full width on mobile */}
                          <h4 
                            className="font-medium text-base mb-1"
                            style={{ color: colors.textPrimary }}
                          >
                            {goal.title}
                          </h4>
                          
                          {goal.specific && (
                            <p 
                              className="text-xs mb-3 line-clamp-2"
                              style={{ color: colors.textMuted }}
                            >
                              {goal.specific}
                            </p>
                          )}
                          
                          {/* Actions row - below title on mobile */}
                          <div className="flex items-center justify-between gap-2">
                            {/* Left: Progress info + Consistency Ring */}
                            <div className="flex items-center gap-3">
                              {hasMilestones && goal.milestones?.length > 0 && (
                                <span className="text-xs" style={{ color: colors.textMuted }}>
                                  {goal.milestones.filter(m => m.completed).length}/{goal.milestones.length} milestones
                                </span>
                              )}
                              
                              {/* Consistency Ring */}
                              {goal.linkedHabitIds && goal.linkedHabitIds.length > 0 ? (
                                <div className="flex items-center gap-2">
                                  <ConsistencyRing
                                    percentage={calculateGoalConsistency(completions, goal.linkedHabitIds, 7)}
                                    size={28}
                                    strokeWidth={2.5}
                                    showLabel={true}
                                    interactive={true}
                                    onClick={() => setLinkedHabitsModalGoal(goal)}
                                  />
                                  <span className="text-[10px]" style={{ color: colors.textMuted }}>
                                    {goal.linkedHabitIds.length} habit{goal.linkedHabitIds.length > 1 ? 's' : ''}
                                  </span>
                                </div>
                              ) : (
                                <LinkHabitsPrompt
                                  size={28}
                                  onClick={() => openEditModal(goal)}
                                  colors={colors}
                                />
                              )}
                            </div>
                            
                            {/* Action buttons */}
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <button
                                onClick={(e) => {
                                  // Trigger bounce animation
                                  e.currentTarget.classList.add('animate-checkbox-bounce');
                                  setTimeout(() => e.currentTarget.classList.remove('animate-checkbox-bounce'), 400);
                                  
                                  // Fade out the card, then archive
                                  const card = e.currentTarget.closest('[data-goal-card]');
                                  if (card) {
                                    card.style.transition = 'opacity 0.45s ease, transform 0.45s ease';
                                    card.style.opacity = '0';
                                    card.style.transform = 'scale(0.96)';
                                  }
                                  setTimeout(() => archiveGoal(goal.id), 450);
                                }}
                                className="p-2 rounded-lg transition-all animate-success-glow"
                                style={{ color: progress === 100 ? colors.accent : colors.textMuted, backgroundColor: `${colors.bgTertiary}50` }}
                                title="Mark complete"
                              >
                                <Check size={16} />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); openEditModal(goal); }}
                                className="p-2 rounded-lg transition-all"
                                style={{ color: colors.textMuted, backgroundColor: `${colors.bgTertiary}50` }}
                                title="Edit"
                              >
                                <Edit2 size={16} />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleDelete(goal.id); }}
                                className="p-2 rounded-lg transition-all"
                                style={{ color: colors.error, backgroundColor: `${colors.error}10` }}
                                title="Delete"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                          
                          {/* Progress bar */}
                          <div className="flex items-center gap-3 mt-3">
                            <div 
                              className="flex-1 h-[2px] overflow-hidden"
                              style={{ backgroundColor: colors.border }}
                            >
                              <div 
                                className="h-full rounded-full transition-all duration-200"
                                style={{ 
                                  width: `${progress}%`,
                                  backgroundColor: colors.textPrimary
                                }}
                              />
                            </div>
                          </div>
                          
                          {/* Expand toggle for details */}
                          {((hasMilestones && goal.milestones?.length > 0) || goal.measurable) && (
                            <button
                              onClick={() => setExpandedGoal(isExpanded ? null : goal.id)}
                              className="mt-3 flex items-center gap-1.5 text-xs transition-all duration-200 hover:opacity-70"
                              style={{ color: colors.accent }}
                            >
                              <ChevronDown 
                                size={14} 
                                style={{ 
                                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                  transition: 'transform 0.2s ease'
                                }} 
                              />
                              <span>{isExpanded ? 'Hide details' : 'Show details'}</span>
                            </button>
                          )}
                          
                          {/* Expanded details */}
                          <div 
                            className="transition-all duration-150 ease-in-out"
                            style={{ 
                              maxHeight: isExpanded ? '350px' : '0',
                              opacity: isExpanded ? 1 : 0,
                              overflowY: isExpanded ? 'auto' : 'hidden',
                              overflowX: 'hidden'
                            }}
                          >
                            <div className="mt-3 pt-3 space-y-2" style={{ borderTop: `1px solid ${colors.border}` }}>
                              {/* SMART Details */}
                              {SMART_CRITERIA.map(({ key, label }) => {
                                const value = goal[key];
                                if (!value || key === 'specific') return null; // specific shown above
                                return (
                                  <div key={key} className="p-2 rounded-lg" style={{ backgroundColor: colors.bgSecondary }}>
                                    <span className="text-xs font-medium" style={{ color: colors.accent }}>
                                      {label}
                                    </span>
                                    <p className="text-xs mt-0.5" style={{ color: colors.textSecondary }}>
                                      {value}
                                    </p>
                                  </div>
                                );
                              })}
                              
                              {/* Milestones */}
                              {hasMilestones && goal.milestones && goal.milestones.length > 0 && (
                                <div className="space-y-2">
                                  <span className="text-xs font-medium" style={{ color: colors.textMuted }}>
                                    Milestones
                                  </span>
                                  {goal.milestones.map(milestone => (
                                    <div 
                                      key={milestone.id} 
                                      className="flex items-center gap-2 p-2 rounded-lg cursor-pointer hover:bg-opacity-50 transition-all"
                                      style={{ backgroundColor: colors.bgSecondary }}
                                      onClick={() => toggleMilestone(goal.id, milestone.id)}
                                    >
                                      <div 
                                        className="w-4 h-4 rounded flex items-center justify-center transition-all"
                                        style={{ 
                                          backgroundColor: milestone.completed ? colors.accent : 'transparent',
                                          border: `1.5px solid ${milestone.completed ? colors.accent : colors.border}`
                                        }}
                                      >
                                        {milestone.completed && <Check size={10} color={colors.onAccent} />}
                                      </div>
                                      <span 
                                        className={`text-sm ${milestone.completed ? 'line-through' : ''}`}
                                        style={{ color: milestone.completed ? colors.textMuted : colors.textSecondary }}
                                      >
                                        {milestone.title}
                                      </span>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); deleteMilestone(goal.id, milestone.id); }}
                                        className="ml-auto p-1 rounded transition-all"
                                        style={{ color: colors.error }}
                                      >
                                        <Trash2 size={12} />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                              
                              {/* Add Milestone */}
                              {hasMilestones && (
                                <div className="pt-2">
                                  {showMilestoneInput === goal.id ? (
                                    <div className="flex gap-2">
                                      <input
                                        type="text"
                                        value={milestoneTitle}
                                        onChange={(e) => setMilestoneTitle(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleAddMilestone(goal.id)}
                                        placeholder="Milestone title..."
                                        className="flex-1 px-3 py-2 text-sm rounded-lg outline-none"
                                        style={{ 
                                          backgroundColor: colors.bgSecondary,
                                          color: colors.textPrimary,
                                          border: `1px solid ${colors.border}`
                                        }}
                                        autoFocus
                                      />
                                      <button
                                        onClick={() => handleAddMilestone(goal.id)}
                                        className="px-3 py-2 rounded-lg text-sm"
                                        style={{ backgroundColor: colors.accent, color: colors.onAccent }}
                                      >
                                        Add
                                      </button>
                                      <button
                                        onClick={() => { setShowMilestoneInput(null); setMilestoneTitle(''); }}
                                        className="px-2 py-2 rounded-lg"
                                        style={{ color: colors.textMuted }}
                                      >
                                        <X size={14} />
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => setShowMilestoneInput(goal.id)}
                                      className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all "
                                      style={{ backgroundColor: colors.bgSecondary, color: colors.accent, border: `1px solid ${colors.accent}30` }}
                                    >
                                      <Plus size={12} />
                                      Add Milestone
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : viewMode === 'board' ? (
          /* Board View - Kanban-style board grouped by progress */
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {(() => {
              const columns = [
                { 
                  title: 'Not Started', 
                  filter: (g) => getProgress(g) === 0,
                  goals: sortedGoals.filter(g => getProgress(g) === 0),
                  color: colors.textMuted,
                  bgColor: colors.bgSecondary
                },
                { 
                  title: 'In Progress', 
                  filter: (g) => getProgress(g) > 0 && getProgress(g) < 100,
                  goals: sortedGoals.filter(g => getProgress(g) > 0 && getProgress(g) < 100),
                  color: colors.warning,
                  bgColor: colors.bgSecondary
                },
                { 
                  title: 'Completed', 
                  filter: (g) => getProgress(g) === 100,
                  // Include both 100% progress goals AND archived goals
                  goals: [...sortedGoals.filter(g => getProgress(g) === 100), ...archivedGoals],
                  color: colors.accent,
                  bgColor: colors.bgSecondary,
                  isCompleted: true
                }
              ];
              
              return columns.map(column => {
                const columnGoals = column.goals;
                
                return (
                  <div 
                    key={column.title}
                    className="rounded-lg p-4"
                    style={{ backgroundColor: column.bgColor, border: `1px solid ${colors.border}` }}
                  >
                    <div className="flex items-center gap-2 mb-4 pb-3" style={{ borderBottom: `1px solid ${colors.border}` }}>
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: column.color }}
                      />
                      <h4 className="font-medium text-sm" style={{ color: colors.textPrimary }}>
                        {column.title}
                      </h4>
                      <span 
                        className="text-xs px-1.5 py-0.5 rounded-full ml-auto"
                        style={{ backgroundColor: colors.bgTertiary, color: colors.textMuted }}
                      >
                        {columnGoals.length}
                      </span>
                    </div>
                    
                    <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                      {columnGoals.length === 0 ? (
                        <p className="text-center text-xs py-4" style={{ color: colors.textMuted }}>
                          No goals here
                        </p>
                      ) : (
                        columnGoals.map(goal => {
                          const progress = getProgress(goal);
                          const overdue = isOverdue(goal);
                          const isExpanded = expandedGoal === goal.id;
                          const isArchived = archivedGoals.some(g => g.id === goal.id);
                          
                          return (
                            <div
                              key={goal.id}
                              data-goal-card
                              className="p-3 transition-colors duration-150"
                              style={{ 
                                backgroundColor: colors.bg,
                                border: `1px solid ${colors.border}`
                              }}
                            >
                              {/* Header with title and actions */}
                              <div className="flex items-start justify-between gap-2 mb-2">
                                <div className="flex items-start gap-2 flex-1 min-w-0">
                                  {/* Complete checkbox (only for non-archived) */}
                                  {!isArchived && (
                                    <button
                                      onClick={(e) => {
                                        const card = e.currentTarget.closest('[data-goal-card]');
                                        if (card) {
                                          card.style.transition = 'opacity 0.45s ease, transform 0.45s ease';
                                          card.style.opacity = '0';
                                          card.style.transform = 'scale(0.96)';
                                        }
                                        setTimeout(() => archiveGoal(goal.id), 450);
                                      }}
                                      onMouseEnter={() => setHoveredGoalId(goal.id)}
                                      onMouseLeave={() => setHoveredGoalId(null)}
                                      className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center transition-all duration-200 flex-shrink-0"
                                      style={{
                                        backgroundColor: hoveredGoalId === goal.id ? colors.accent : 'transparent',
                                        border: `1.5px solid ${hoveredGoalId === goal.id ? colors.accent : colors.border}`
                                      }}
                                      title="Mark as complete"
                                    >
                                      {hoveredGoalId === goal.id && <Check size={10} color={colors.onAccent} />}
                                    </button>
                                  )}
                                  {isArchived && (
                                    <div 
                                      className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                                      style={{ backgroundColor: colors.accent }}
                                    >
                                      <Check size={10} color={colors.onAccent} />
                                    </div>
                                  )}
                                  <h5 
                                    className="font-medium text-sm"
                                    style={{ 
                                      color: colors.textPrimary,
                                      textDecoration: hoveredGoalId === goal.id ? 'line-through' : 'none',
                                      opacity: hoveredGoalId === goal.id ? 0.6 : 1
                                    }}
                                  >
                                    {goal.title}
                                  </h5>
                                </div>
                                
                                {/* Actions */}
                                <div className="flex items-center gap-0.5 flex-shrink-0">
                                  {isArchived ? (
                                    <button
                                      onClick={() => {
                                        setShowArchivedModal(true);
                                      }}
                                      className="p-1.5 rounded-lg transition-all"
                                      style={{ color: colors.accent }}
                                      title="Restore"
                                    >
                                      <RotateCcw size={14} />
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => openEditModal(goal)}
                                      className="p-1.5 rounded-lg transition-all"
                                      style={{ color: colors.textMuted }}
                                      title="Edit"
                                    >
                                      <Edit2 size={14} />
                                    </button>
                                  )}
                                  <button
                                    onClick={() => isArchived ? permanentlyDeleteGoal(goal.id) : handleDelete(goal.id)}
                                    className="p-1.5 rounded-lg transition-all"
                                    style={{ color: colors.error }}
                                    title="Delete"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </div>
                              
                              {/* Progress bar (for non-archived) */}
                              {!isArchived && goal.milestones && goal.milestones.length > 0 && (
                                <div className="flex items-center gap-2 mb-2">
                                  <div 
                                    className="flex-1 h-[2px] overflow-hidden"
                                    style={{ backgroundColor: colors.border }}
                                  >
                                    <div 
                                      className="h-full rounded-full transition-all"
                                      style={{ 
                                        width: `${progress}%`,
                                        backgroundColor: colors.textPrimary
                                      }}
                                    />
                                  </div>
                                  <span className="text-[10px]" style={{ color: colors.textMuted }}>
                                    {goal.milestones.filter(m => m.completed).length}/{goal.milestones.length}
                                  </span>
                                </div>
                              )}
                              
                              {/* Show incomplete milestones preview for Not Started and In Progress */}
                              {!isArchived && !column.isCompleted && hasMilestones && goal.milestones && goal.milestones.length > 0 && (
                                <div className="space-y-1 mb-2">
                                  {goal.milestones.filter(m => !m.completed).slice(0, 3).map(milestone => (
                                    <div 
                                      key={milestone.id}
                                      className="flex items-center gap-1.5 text-[10px] cursor-pointer hover:opacity-70 transition-all"
                                      onClick={() => toggleMilestone(goal.id, milestone.id)}
                                    >
                                      <div 
                                        className="w-2.5 h-2.5 rounded flex-shrink-0"
                                        style={{ border: `1px solid ${colors.border}` }}
                                      />
                                      <span className="truncate" style={{ color: colors.textMuted }}>
                                        {milestone.title}
                                      </span>
                                    </div>
                                  ))}
                                  {goal.milestones.filter(m => !m.completed).length > 3 && (
                                    <span className="text-[9px]" style={{ color: colors.textMuted }}>
                                      +{goal.milestones.filter(m => !m.completed).length - 3} more
                                    </span>
                                  )}
                                </div>
                              )}
                              
                              {/* Date info + Consistency Ring */}
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2 flex-wrap">
                                  {goal.targetDate && (
                                    <span 
                                      className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded"
                                      style={{ 
                                        backgroundColor: overdue && !isArchived ? `${colors.error}15` : colors.bgTertiary,
                                        color: overdue && !isArchived ? colors.error : colors.textMuted 
                                      }}
                                    >
                                      <Calendar size={9} />
                                      {formatDate(goal.targetDate)}
                                    </span>
                                  )}
                                  {isArchived && goal.completedAt && (
                                    <span className="text-[10px]" style={{ color: colors.textMuted }}>
                                      ✓ {formatDate(goal.completedAt)}
                                    </span>
                                  )}
                                </div>
                                
                                {/* Consistency Ring */}
                                {!isArchived && (
                                  goal.linkedHabitIds && goal.linkedHabitIds.length > 0 ? (
                                    <div className="flex items-center gap-1.5">
                                      <ConsistencyRing
                                        percentage={calculateGoalConsistency(completions, goal.linkedHabitIds, 7)}
                                        size={24}
                                        strokeWidth={2}
                                        showLabel={true}
                                        interactive={true}
                                        onClick={() => setLinkedHabitsModalGoal(goal)}
                                      />
                                      <span className="text-[9px]" style={{ color: colors.textMuted }}>
                                        {goal.linkedHabitIds.length}
                                      </span>
                                    </div>
                                  ) : (
                                    <LinkHabitsPrompt
                                      size={24}
                                      onClick={() => openEditModal(goal)}
                                      colors={colors}
                                    />
                                  )
                                )}
                              </div>
                              
                              {/* Expand toggle */}
                              {!isArchived && (hasMilestones || goal.specific || goal.measurable) && (
                                <button
                                  onClick={() => setExpandedGoal(isExpanded ? null : goal.id)}
                                  className="mt-2 flex items-center gap-1 text-[10px] transition-all hover:opacity-70"
                                  style={{ color: colors.accent }}
                                >
                                  {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                                  {isExpanded ? 'Hide' : 'Details'}
                                </button>
                              )}
                              
                              {/* Expanded content with smooth animation */}
                              <div 
                                className="transition-all duration-150 ease-in-out"
                                style={{ 
                                  maxHeight: isExpanded ? '300px' : '0',
                                  opacity: isExpanded ? 1 : 0,
                                  overflowY: isExpanded ? 'auto' : 'hidden',
                                  overflowX: 'hidden'
                                }}
                              >
                                <div className="mt-3 pt-3 space-y-2" style={{ borderTop: `1px solid ${colors.border}` }}>
                                  {/* SMART Details */}
                                  {SMART_CRITERIA.map(({ key, label }) => {
                                    const value = goal[key];
                                    if (!value) return null;
                                    return (
                                      <div key={key} className="p-2 rounded-lg" style={{ backgroundColor: colors.bgSecondary }}>
                                        <span className="text-xs font-medium" style={{ color: colors.accent }}>
                                          {label}
                                        </span>
                                        <p className="text-xs mt-0.5" style={{ color: colors.textSecondary }}>
                                          {value}
                                        </p>
                                      </div>
                                    );
                                  })}
                                  
                                  {/* Milestones */}
                                  {hasMilestones && goal.milestones && goal.milestones.length > 0 && (
                                    <div className="space-y-1">
                                      <span className="text-xs font-medium" style={{ color: colors.textMuted }}>
                                        Milestones
                                      </span>
                                      {goal.milestones.map(milestone => (
                                        <div 
                                          key={milestone.id} 
                                          className="flex items-center gap-1.5 p-1.5 rounded cursor-pointer hover:bg-opacity-50 transition-all"
                                          style={{ backgroundColor: colors.bgSecondary }}
                                          onClick={() => toggleMilestone(goal.id, milestone.id)}
                                        >
                                          <div 
                                            className="w-3 h-3 rounded flex items-center justify-center"
                                            style={{ 
                                              backgroundColor: milestone.completed ? colors.accent : 'transparent',
                                              border: `1px solid ${milestone.completed ? colors.accent : colors.border}`
                                            }}
                                          >
                                            {milestone.completed && <Check size={8} color={colors.onAccent} />}
                                          </div>
                                          <span 
                                            className={`text-[11px] flex-1 ${milestone.completed ? 'line-through' : ''}`}
                                            style={{ color: milestone.completed ? colors.textMuted : colors.textSecondary }}
                                          >
                                            {milestone.title}
                                          </span>
                                          <button
                                            onClick={(e) => { e.stopPropagation(); deleteMilestone(goal.id, milestone.id); }}
                                            className="p-0.5 rounded transition-all"
                                            style={{ color: colors.error }}
                                          >
                                            <Trash2 size={10} />
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  
                                  {/* Add Milestone */}
                                  {hasMilestones && !isArchived && (
                                    <div className="pt-1">
                                      {showMilestoneInput === goal.id ? (
                                        <div className="flex gap-1">
                                          <input
                                            type="text"
                                            value={milestoneTitle}
                                            onChange={(e) => setMilestoneTitle(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && handleAddMilestone(goal.id)}
                                            placeholder="Milestone..."
                                            className="flex-1 px-2 py-1 text-xs rounded outline-none"
                                            style={{ 
                                              backgroundColor: colors.bgSecondary,
                                              color: colors.textPrimary,
                                              border: `1px solid ${colors.border}`
                                            }}
                                            autoFocus
                                          />
                                          <button
                                            onClick={() => handleAddMilestone(goal.id)}
                                            className="px-2 py-1 rounded text-xs"
                                            style={{ backgroundColor: colors.accent, color: colors.onAccent }}
                                          >
                                            Add
                                          </button>
                                        </div>
                                      ) : (
                                        <button
                                          onClick={() => setShowMilestoneInput(goal.id)}
                                          className="text-[10px] flex items-center gap-1 px-2 py-1 rounded transition-all "
                                          style={{ backgroundColor: colors.bgSecondary, color: colors.accent }}
                                        >
                                          <Plus size={10} />
                                          Add Milestone
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        ) : (
          /* List View - compact notebook cards, several per row; milestones check off in place */
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-x-8 gap-y-10">
            {sortedGoals.map((goal, index) => {
              const isExpanded = expandedGoal === goal.id;
              const progress = getProgress(goal);
              const overdue = isOverdue(goal);
              const isLocked = goalLimit !== Infinity && index >= goalLimit;
              const milestones = goal.milestones || [];
              const doneCount = milestones.filter(m => m.completed).length;
              const smart = SMART_CRITERIA.filter(({ key }) => goal[key]);
              const consistency = goal.linkedHabitIds?.length
                ? calculateGoalConsistency(completions, goal.linkedHabitIds, 7)
                : null;
              const rule = `1px solid ${colors.border}`;

              return (
                <article
                  key={goal.id}
                  data-goal-card
                  className="relative flex flex-col min-w-0 transition-opacity duration-150"
                  style={{ borderTop: `1px solid ${colors.textPrimary}`, opacity: isLocked ? 0.4 : 1 }}
                >
                  {isLocked && (
                    <button
                      className="absolute inset-0 z-10 flex items-start justify-end pt-3"
                      onClick={() => { window.location.href = '/upgrade'; }}
                      title="Upgrade to Pro to unlock this goal"
                      aria-label={`Upgrade to Pro to unlock ${goal.title}`}
                    >
                      <Lock size={16} style={{ color: colors.textMuted }} />
                    </button>
                  )}

                  <div className="flex items-start gap-3 pt-3">
                    <h3 className="flex-1 min-w-0 text-[17px] font-semibold tracking-tight leading-snug" style={{ color: colors.textPrimary }}>
                      {goal.title}
                    </h3>
                    <span className="font-mono text-2xl font-light tabular-nums leading-none" style={{ color: colors.textPrimary, letterSpacing: '-0.03em' }}>
                      {progress}%
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 font-mono text-xs" style={{ color: colors.textSecondary }}>
                    {goal.targetDate && (
                      <span style={overdue ? { color: colors.textPrimary, textDecoration: 'underline', textUnderlineOffset: 3 } : undefined}>
                        {overdue ? 'overdue' : 'due'} {formatDate(goal.targetDate)}
                      </span>
                    )}
                    {milestones.length > 0 && <span>{doneCount}/{milestones.length} milestones</span>}
                    {consistency !== null && (
                      <button
                        onClick={() => setLinkedHabitsModalGoal(goal)}
                        className="hover:underline underline-offset-4"
                        title="Linked habits, last 7 days"
                      >
                        habits {consistency}% · 7d
                      </button>
                    )}
                  </div>

                  <div className="mt-3 h-[2px]" style={{ backgroundColor: colors.border }}>
                    <div className="h-full transition-[width] duration-300" style={{ width: `${progress}%`, backgroundColor: colors.textPrimary }} />
                  </div>

                  {hasMilestones ? (
                    <div className="mt-3">
                      {milestones.length > 0 && (
                        <ul className="max-h-[228px] overflow-y-auto overscroll-contain" style={{ borderTop: rule }}>
                          {milestones.map(milestone => (
                            <li key={milestone.id} className="flex items-center group" style={{ borderBottom: rule }}>
                              <button
                                onClick={() => !isLocked && toggleMilestone(goal.id, milestone.id)}
                                disabled={isLocked}
                                aria-pressed={!!milestone.completed}
                                className="flex-1 min-w-0 flex items-center gap-2.5 min-h-[40px] py-2 pr-1 text-left notebook-row-hover"
                              >
                                <InkBox checked={!!milestone.completed} size={16} />
                                <span
                                  className={`ink-strike text-sm leading-snug ${milestone.completed ? 'on' : ''}`}
                                  style={{ color: milestone.completed ? colors.textMuted : colors.textPrimary }}
                                >
                                  {milestone.title}
                                </span>
                              </button>
                              {milestone.dueDate && (
                                <span className="font-mono text-[11px] px-1 flex-shrink-0" style={{ color: colors.textMuted }}>
                                  {formatDate(milestone.dueDate)}
                                </span>
                              )}
                              <button
                                onClick={() => deleteMilestone(goal.id, milestone.id)}
                                className="p-2 opacity-100 md:opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                                style={{ color: colors.textMuted }}
                                aria-label={`Delete milestone ${milestone.title}`}
                              >
                                <X size={13} />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                      {showMilestoneInput === goal.id ? (
                        <div className="flex items-center gap-2" style={{ borderBottom: `1px solid ${colors.textPrimary}` }}>
                          <Plus size={13} style={{ color: colors.textMuted }} />
                          <input
                            type="text"
                            value={milestoneTitle}
                            onChange={(e) => setMilestoneTitle(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleAddMilestone(goal.id);
                              if (e.key === 'Escape') setShowMilestoneInput(null);
                            }}
                            onBlur={() => { if (!milestoneTitle.trim()) setShowMilestoneInput(null); }}
                            placeholder="New milestone, press Enter"
                            aria-label={`New milestone for ${goal.title}`}
                            className="flex-1 min-w-0 bg-transparent outline-none text-base sm:text-sm py-2.5"
                            style={{ color: colors.textPrimary }}
                            autoFocus
                          />
                        </div>
                      ) : (
                        !isLocked && (
                          <button
                            onClick={() => { setMilestoneTitle(''); setShowMilestoneInput(goal.id); }}
                            className="flex items-center gap-2 min-h-[36px] font-mono text-xs hover:underline underline-offset-4"
                            style={{ color: colors.textSecondary }}
                          >
                            <Plus size={12} />
                            Add milestone
                          </button>
                        )
                      )}
                    </div>
                  ) : (
                    <a href="/upgrade" className="mt-3 font-mono text-xs underline underline-offset-4" style={{ color: colors.textSecondary }}>
                      Milestones are a Pro feature
                    </a>
                  )}

                  {isExpanded && smart.length > 0 && (
                    <dl className="mt-3 grid gap-2.5 text-sm">
                      {smart.map(({ key, label }) => (
                        <div key={key}>
                          <dt className="font-mono text-[11px]" style={{ color: colors.textMuted }}>{label}</dt>
                          <dd className="mt-0.5 leading-snug" style={{ color: colors.textSecondary }}>{goal[key]}</dd>
                        </div>
                      ))}
                    </dl>
                  )}

                  <div className="flex items-center gap-4 mt-auto pt-3 font-mono text-xs" style={{ color: colors.textSecondary }}>
                    {smart.length > 0 && (
                      <button onClick={() => setExpandedGoal(isExpanded ? null : goal.id)} className="hover:underline underline-offset-4" aria-expanded={isExpanded}>
                        {isExpanded ? 'Hide details' : 'Details'}
                      </button>
                    )}
                    {!goal.linkedHabitIds?.length && (
                      <button onClick={() => openEditModal(goal)} className="hover:underline underline-offset-4">Link habits</button>
                    )}
                    <span className="flex-1" />
                    <button
                      onClick={(e) => {
                        if (isLocked) return;
                        const card = e.currentTarget.closest('[data-goal-card]');
                        if (card) {
                          card.style.transition = 'opacity 0.45s ease';
                          card.style.opacity = '0';
                        }
                        setTimeout(() => archiveGoal(goal.id), 450);
                      }}
                      disabled={isLocked}
                      className="hover:underline underline-offset-4"
                      title="Mark this goal complete"
                    >
                      Complete
                    </button>
                    <button onClick={() => openEditModal(goal)} className="p-1 transition-opacity hover:opacity-60" aria-label={`Edit ${goal.title}`}>
                      <Edit2 size={13} strokeWidth={1.5} />
                    </button>
                    <button onClick={() => handleDelete(goal.id)} className="p-1 transition-opacity hover:opacity-60" aria-label={`Delete ${goal.title}`}>
                      <Trash2 size={13} strokeWidth={1.5} />
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Add/Edit Goal Modal */}
      {showModal && createPortal(
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black/60 animate-fade-in">
          <div 
            className="sheet-safe w-full h-full sm:h-auto sm:max-w-2xl sm:rounded-lg flex flex-col animate-scale-up"
            style={{ 
              ...getModalStyle(colors),
              maxHeight: 'calc(100vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
              paddingTop: 'env(safe-area-inset-top, 0px)'
            }}
          >
            {/* Fixed Header */}
            <div className="flex items-center justify-between p-5 sm:p-8 pb-0 flex-shrink-0">
              <h3 
                className="text-xl font-semibold tracking-tight"
                style={{ color: colors.textPrimary }}
              >
                {editingGoal ? 'Edit Goal' : (smartModeEnabled && hasSmartGoals ? 'Create SMART Goal' : 'Create Goal')}
              </h3>
              <button 
                onClick={() => setShowModal(false)} 
                className="p-2 -mr-2 rounded-full"
                style={{ color: colors.textMuted }}
              >
                <X size={24} />
              </button>
            </div>
            
            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-5 sm:p-8 pt-4 sm:pt-6 space-y-5">
              {/* Title with rotating placeholder */}
              <div>
                <label className="block text-xs mb-2 font-medium" style={{ color: colors.textMuted }}>
                  Goal Title
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={goalForm.title}
                    onChange={(e) => setGoalForm({ ...goalForm, title: e.target.value })}
                    className="w-full px-4 py-4 sm:py-3 text-base rounded-md outline-none transition-all duration-200 focus:ring-2"
                    style={{ 
                      backgroundColor: colors.bg,
                      color: colors.textPrimary,
                      border: `1px solid ${colors.border}`,
                    }}
                  />
                  {/* Animated placeholder */}
                  {!goalForm.title && (
                    <div className="absolute inset-0 flex items-center px-4 pointer-events-none overflow-hidden">
                      <span 
                        key={goalSuggestionIndex}
                        className="text-base transition-all duration-200 font-semibold tracking-tight"
                        style={{ 
                          color: colors.textMuted,
                          animation: 'fadeInUp 0.5s ease-out' }}
                      >
                        {GOAL_SUGGESTIONS[goalSuggestionIndex]}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Target Date */}
              <div>
                <label className="block text-xs mb-2 font-medium" style={{ color: colors.textMuted }}>
                  Target Date
                </label>
                <input
                  type="date"
                  value={goalForm.targetDate}
                  onChange={(e) => setGoalForm({ ...goalForm, targetDate: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full py-4 sm:py-3 rounded-md outline-none transition-all duration-200 focus:ring-2"
                  style={{ 
                    backgroundColor: colors.bg,
                    color: colors.textPrimary,
                    border: `1px solid ${colors.border}`,
                    minWidth: 0,
                    fontSize: '14px',
                    paddingLeft: '12px',
                    paddingRight: '8px',
                    WebkitAppearance: 'none'
                  }}
                />
                {/* Quick date pills - always visible, highlighted when selected */}
                <div className="flex gap-2 mt-2 flex-wrap">
                  {[
                    { label: '1 month', months: 1 },
                    { label: '3 months', months: 3 },
                    { label: '6 months', months: 6 },
                    { label: '1 year', months: 12 },
                  ].map((option) => {
                    const date = new Date();
                    date.setMonth(date.getMonth() + option.months);
                    const dateStr = date.toISOString().split('T')[0];
                    // Check if target date is approximately the same (within a few days for month calculations)
                    const targetDate = goalForm.targetDate ? new Date(goalForm.targetDate) : null;
                    const optionDate = new Date(dateStr);
                    const isApproxMatch = targetDate && 
                      Math.abs(targetDate.getTime() - optionDate.getTime()) < 3 * 24 * 60 * 60 * 1000; // within 3 days
                    
                    return (
                      <button
                        key={option.months}
                        onClick={() => {
                          // Toggle: if approximately selected, clear; otherwise set
                          setGoalForm({ 
                            ...goalForm, 
                            targetDate: isApproxMatch ? '' : dateStr 
                          });
                        }}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 "
                        style={{
                          backgroundColor: isApproxMatch ? colors.accent : colors.bgTertiary,
                          color: isApproxMatch ? colors.onAccent : colors.textSecondary,
                          border: `2px solid ${isApproxMatch ? colors.accent : colors.border}`,
                        }}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {/* Milestones Section */}
              {hasMilestones && (
                <div>
                  <label className="block text-xs mb-2 font-medium" style={{ color: colors.textMuted }}>
                    Milestones (Optional)
                  </label>
                  
                  {/* Add Milestone Input */}
                  <div className="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={newMilestoneTitle}
                      onChange={(e) => setNewMilestoneTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newMilestoneTitle.trim()) {
                          e.preventDefault();
                          const newMilestone = {
                            id: `ms${Date.now()}`,
                            title: newMilestoneTitle.trim(),
                            completed: false
                          };
                          setGoalForm({ ...goalForm, milestones: [...goalForm.milestones, newMilestone] });
                          setNewMilestoneTitle('');
                        }
                      }}
                      placeholder="Add a milestone..."
                      className="flex-1 px-4 py-2.5 text-sm rounded-md outline-none transition-all duration-200 focus:ring-2"
                      style={{ 
                        backgroundColor: colors.bg,
                        color: colors.textPrimary,
                        border: `1px solid ${colors.border}`
                      }}
                    />
                    <button
                      onClick={() => {
                        if (!newMilestoneTitle.trim()) return;
                        const newMilestone = {
                          id: `ms${Date.now()}`,
                          title: newMilestoneTitle.trim(),
                          completed: false
                        };
                        setGoalForm({ ...goalForm, milestones: [...goalForm.milestones, newMilestone] });
                        setNewMilestoneTitle('');
                      }}
                      className="px-4 py-2.5 rounded-md transition-all duration-200 "
                      style={{ backgroundColor: colors.accent, color: colors.onAccent }}
                    >
                      <Plus size={18} />
                    </button>
                  </div>
                  
                  {/* Milestone List */}
                  {goalForm.milestones.length > 0 && (
                    <div className="space-y-2">
                      {goalForm.milestones.map((milestone, index) => (
                        <div
                          key={milestone.id}
                          className="flex items-center gap-3 p-3 rounded-md"
                          style={{ backgroundColor: colors.bg }}
                        >
                          <span 
                            className="text-sm flex-1"
                            style={{ color: colors.textPrimary }}
                          >
                            {milestone.title}
                          </span>
                          <button
                            onClick={() => {
                              const updated = goalForm.milestones.filter((_, i) => i !== index);
                              setGoalForm({ ...goalForm, milestones: updated });
                            }}
                            className="p-1.5 rounded-lg transition-all duration-200 "
                            style={{ color: colors.textMuted }}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {goalForm.milestones.length === 0 && (
                    <p className="text-xs text-center py-3" style={{ color: colors.textMuted }}>
                      Break your goal into smaller steps to track progress
                    </p>
                  )}
                </div>
              )}
              
              {/* Linked Habits Section */}
              <div>
                <label className="block text-xs mb-2 font-medium" style={{ color: colors.textMuted }}>
                  <span className="flex items-center gap-1.5">
                    <Link2 size={12} />
                    Linked Habits (Track consistency)
                  </span>
                </label>
                
                {activeHabits.length === 0 ? (
                  <p className="text-xs text-center py-3" style={{ color: colors.textMuted }}>
                    No habits yet. Create habits first to link them.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {activeHabits.map(habit => {
                      const isSelected = selectedHabitIds.includes(habit.id);
                      return (
                        <button
                          key={habit.id}
                          type="button"
                          onClick={() => {
                            if (isSelected) {
                              setSelectedHabitIds(selectedHabitIds.filter(id => id !== habit.id));
                            } else {
                              setSelectedHabitIds([...selectedHabitIds, habit.id]);
                            }
                          }}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-all duration-200"
                          style={{
                            backgroundColor: isSelected ? colors.accent : colors.bg,
                            color: isSelected ? colors.onAccent : colors.textSecondary,
                            border: `1px solid ${isSelected ? colors.accent : colors.border}`
                          }}
                        >
                          {isSelected && <Check size={12} />}
                          <Flame size={12} />
                          {habit.name}
                        </button>
                      );
                    })}
                  </div>
                )}
                
                {selectedHabitIds.length > 0 && (
                  <p className="text-xs mt-2" style={{ color: colors.textMuted }}>
                    {selectedHabitIds.length} habit{selectedHabitIds.length > 1 ? 's' : ''} linked — consistency will be tracked
                  </p>
                )}
              </div>
              
              {/* SMART details: the explanation sits with the choice, not on the page */}
              <button
                type="button"
                onClick={() => (hasSmartGoals ? setSmartModeEnabled(!smartModeEnabled) : setShowUpgradeModal(true))}
                aria-pressed={smartModeEnabled && hasSmartGoals}
                className="w-full flex items-start gap-3 py-3 text-left"
                style={{ borderTop: `1px solid ${colors.border}`, borderBottom: `1px solid ${colors.border}` }}
              >
                <span className="pt-0.5">
                  {hasSmartGoals ? <InkBox checked={smartModeEnabled} /> : <Lock size={16} style={{ color: colors.textMuted }} />}
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium" style={{ color: colors.textPrimary }}>
                    Add SMART details{!hasSmartGoals && <span className="font-mono text-xs ml-2" style={{ color: colors.textMuted }}>Pro</span>}
                  </span>
                  <span className="block text-xs mt-0.5" style={{ color: colors.textMuted }}>
                    Goals are more achievable when they are Specific, Measurable, Achievable, Relevant, and Time-bound.
                  </span>
                </span>
              </button>
              
              {/* SMART Fields - Only show when enabled */}
              {smartModeEnabled && hasSmartGoals && (
                <div className="space-y-4 animate-fade-in">
                  <div className="flex items-center gap-2">
                    <Sparkles size={14} style={{ color: colors.accent }} />
                    <p className="text-xs font-medium" style={{ color: colors.accent }}>
                      SMART Framework
                    </p>
                  </div>
                  
                  {SMART_CRITERIA.map(({ key, label, question }) => (
                    <div key={key}>
                      <label className="block text-xs mb-2" style={{ color: colors.textSecondary }}>
                        <strong>{label}:</strong> {question}
                      </label>
                      <textarea
                        value={goalForm[key]}
                        onChange={(e) => setGoalForm({ ...goalForm, [key]: e.target.value })}
                        rows={2}
                        className="w-full px-4 py-2.5 text-sm rounded-md outline-none resize-none transition-all duration-200 focus:ring-2"
                        style={{ 
                          backgroundColor: colors.bg,
                          color: colors.textPrimary,
                          border: `1px solid ${colors.border}`
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
              
            </div>
            
            {/* Fixed Footer */}
            <div className="p-5 sm:p-8 pt-4 flex-shrink-0">
              <button
                onClick={handleSave}
                className="w-full py-3.5 text-xs rounded-md transition-all duration-200 font-medium"
                style={{ backgroundColor: colors.accent, color: colors.onAccent }}
              >
                {editingGoal ? 'Save Changes' : 'Create Goal'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Completed Goals Modal */}
      {showArchivedModal && createPortal(
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black/60 animate-fade-in">
          <div 
            className="sheet-safe w-full h-full sm:h-auto sm:max-w-2xl sm:rounded-lg flex flex-col animate-scale-up"
            style={{ 
              ...getModalStyle(colors),
              maxHeight: 'calc(100vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
              paddingTop: 'env(safe-area-inset-top, 0px)'
            }}
          >
            {/* Fixed Header */}
            <div className="flex items-center justify-between p-5 sm:p-6 pb-4 flex-shrink-0" style={{ borderBottom: `1px solid ${colors.border}` }}>
              <h3 
                className="text-xl font-semibold tracking-tight"
                style={{ color: colors.textPrimary }}
              >
                Completed Goals ({archivedGoals.length})
              </h3>
              <button 
                onClick={() => setShowArchivedModal(false)} 
                className="p-2 -mr-2 rounded-full"
                style={{ color: colors.textMuted }}
              >
                <X size={24} />
              </button>
            </div>
            
            {/* Fixed Sort/Filter Controls */}
            <div className="px-6 py-4 flex-shrink-0" style={{ borderBottom: `1px solid ${colors.border}` }}>
              <div className="flex items-center gap-4 flex-wrap">
                {/* Sort */}
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium" style={{ color: colors.textMuted }}>Sort:</span>
                  <select
                    value={archivedSort}
                    onChange={(e) => setArchivedSort(e.target.value)}
                    className="text-sm px-3 py-1.5 rounded-lg outline-none cursor-pointer"
                    style={{ 
                      backgroundColor: colors.bg, 
                      color: colors.textPrimary,
                      border: `1px solid ${colors.border}`
                    }}
                  >
                    <option value="newest">Newest</option>
                    <option value="oldest">Oldest</option>
                    <option value="alphabetical">A-Z</option>
                  </select>
                </div>
              </div>
            </div>
            
            {/* Scrollable Goals List */}
            <div className="flex-1 overflow-y-auto p-6">
              {(() => {
                // Apply filters and sort
                let filtered = [...archivedGoals];
                
                // Sort
                if (archivedSort === 'newest') {
                  filtered.sort((a, b) => new Date(b.completedAt || b.createdAt) - new Date(a.completedAt || a.createdAt));
                } else if (archivedSort === 'oldest') {
                  filtered.sort((a, b) => new Date(a.completedAt || a.createdAt) - new Date(b.completedAt || b.createdAt));
                } else if (archivedSort === 'alphabetical') {
                  filtered.sort((a, b) => a.title.localeCompare(b.title));
                }
                
                if (filtered.length === 0) {
                  return (
                    <div className="text-center py-12">
                      <Target size={48} style={{ color: colors.textMuted }} className="mx-auto mb-4" />
                      <p style={{ color: colors.textMuted }}>
                        No completed goals yet
                      </p>
                    </div>
                  );
                }
                
                return (
                  <div className="space-y-3">
                    {filtered.map(goal => {
                      return (
                        <div
                          key={goal.id}
                          className="p-4 rounded-md transition-all duration-200 "
                          style={{ backgroundColor: colors.bg }}
                        >
                          <div className="flex items-start gap-3">
                            {/* Completed checkmark */}
                            <div 
                              className="mt-0.5 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: colors.accent }}
                            >
                              <Check size={14} color={colors.onAccent} />
                            </div>
                            
                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <h4 
                                className="text-sm font-medium"
                                style={{ color: colors.textPrimary }}
                              >
                                {goal.title}
                              </h4>
                              
                              <div className="flex items-center gap-2 mt-2 flex-wrap">
                                {/* Completed date */}
                                {goal.completedAt && (
                                  <span className="text-xs" style={{ color: colors.textMuted }}>
                                    Completed {formatDate(goal.completedAt)}
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            {/* Actions */}
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <button
                                onClick={() => setRestoreGoalId(restoreGoalId === goal.id ? null : goal.id)}
                                className="p-1.5 rounded-lg transition-all duration-200 "
                                style={{ color: colors.accent }}
                                title="Restore goal"
                              >
                                <RotateCcw size={14} />
                              </button>
                              <button
                                onClick={() => {
                                  permanentlyDeleteGoal(goal.id);
                                }}
                                className="p-1.5 rounded-lg transition-all duration-200 "
                                style={{ color: colors.error }}
                                title="Delete permanently"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                          
                          {/* Restore section - expands below */}
                          {restoreGoalId === goal.id && (
                            <div 
                              className="mt-3 pt-3"
                              style={{ borderTop: `1px solid ${colors.border}` }}
                            >
                              <label className="block text-xs mb-2 font-medium" style={{ color: colors.textMuted }}>
                                New Target Date
                              </label>
                              <input
                                type="date"
                                value={restoreTargetDate}
                                onChange={(e) => setRestoreTargetDate(e.target.value)}
                                className="w-full text-sm px-3 py-2 rounded-lg outline-none mb-2"
                                style={{ 
                                  backgroundColor: colors.bgSecondary,
                                  color: colors.textPrimary,
                                  border: `1px solid ${colors.border}`
                                }}
                              />
                              {/* Quick date pills */}
                              <div className="flex gap-2 flex-wrap mb-3">
                                {[
                                  { label: '1 week', days: 7 },
                                  { label: '1 month', months: 1 },
                                  { label: '3 months', months: 3 },
                                  { label: '6 months', months: 6 },
                                ].map((option) => {
                                  const date = new Date();
                                  if (option.days) {
                                    date.setDate(date.getDate() + option.days);
                                  } else if (option.months) {
                                    date.setMonth(date.getMonth() + option.months);
                                  }
                                  const dateStr = date.toISOString().split('T')[0];
                                  const isSelected = restoreTargetDate === dateStr;
                                  
                                  return (
                                    <button
                                      key={option.label}
                                      onClick={() => setRestoreTargetDate(isSelected ? '' : dateStr)}
                                      className="px-2 py-1 rounded-lg text-[10px] font-medium transition-all duration-200 "
                                      style={{
                                        backgroundColor: isSelected ? colors.accent : colors.bgTertiary,
                                        color: isSelected ? colors.onAccent : colors.textSecondary,
                                        border: `1px solid ${isSelected ? colors.accent : colors.border}`,
                                      }}
                                    >
                                      {option.label}
                                    </button>
                                  );
                                })}
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => {
                                    restoreGoal(goal.id, restoreTargetDate);
                                    setRestoreGoalId(null);
                                    setRestoreTargetDate('');
                                  }}
                                  className="flex-1 text-xs px-3 py-2 rounded-lg font-medium transition-all duration-200 "
                                  style={{ backgroundColor: colors.accent, color: colors.onAccent }}
                                >
                                  Restore
                                </button>
                                <button
                                  onClick={() => {
                                    setRestoreGoalId(null);
                                    setRestoreTargetDate('');
                                  }}
                                  className="text-xs px-3 py-2 rounded-lg"
                                  style={{ color: colors.textMuted, border: `1px solid ${colors.border}` }}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {/* Linked Habits Modal */}
      {linkedHabitsModalGoal && (
        <LinkedHabitsModal
          goal={linkedHabitsModalGoal}
          habits={habits}
          completions={completions}
          onClose={() => setLinkedHabitsModalGoal(null)}
          onEditLinks={() => {
            setLinkedHabitsModalGoal(null);
            openEditModal(linkedHabitsModalGoal);
          }}
          colors={colors}
        />
      )}
      
      {/* Upgrade Limit Modal */}
      <UpgradeLimitModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        itemType="goals"
        currentCount={goals.length}
        limit={goalLimit}
      />
    </div>
  );
};

export default GoalsPage;
